import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { CText, CHeader } from '../../components/common';
import { FiShield, FiCheck, FiX, FiInfo, FiExternalLink, FiUpload } from 'react-icons/fi';
import axiosClient from '../../api/axios';
import { setUser } from '../../store/slices/userSlice';

const SEBI_QUALIFICATIONS = [
  'Investment Adviser',
  'Research Analyst',
  'Portfolio Manager',
  'Mutual Fund Distributor',
  'Stock Broker',
  'Merchant Banker',
  'Other',
];

const SebiCredentials: React.FC = () => {
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  const [regNumber, setRegNumber] = useState(user?.sebi_reg_number || '');
  const [qualification, setQualification] = useState(user?.sebi_qualification || '');
  const [yearsOfXp, setYearsOfXp] = useState(user?.years_of_xp?.toString() || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (user) {
      setRegNumber(user.sebi_reg_number || '');
      setQualification(user.sebi_qualification || '');
      setYearsOfXp(user.years_of_xp?.toString() || '');
    }
  }, [user]);

  const handleSave = async () => {
    if (!regNumber.trim()) {
      setMessage({ type: 'error', text: 'SEBI Registration Number is required' });
      return;
    }
    if (!qualification) {
      setMessage({ type: 'error', text: 'Please select your qualification' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      await axiosClient.post('auth/user_details/', {
        sebi_reg_number: regNumber.trim(),
        sebi_qualification: qualification,
        years_of_xp: parseInt(yearsOfXp) || 0,
      });

      // Update Redux store
      if (user) {
        dispatch(setUser({
          ...user,
          sebi_reg_number: regNumber.trim(),
          sebi_qualification: qualification,
          years_of_xp: parseInt(yearsOfXp) || 0,
        }));
      }

      setMessage({ type: 'success', text: 'Credentials updated successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update credentials' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="p-4 lg:p-8">
        <div className="lg:max-w-2xl lg:mx-auto">
          <CHeader title="SEBI Credentials" />

          {/* Verification Status */}
          <div
            className="mb-6 p-4 rounded-xl flex items-center gap-3"
            style={{
              backgroundColor: user?.is_verified ? colors.green + '15' : colors.alertColor + '15',
              border: `1px solid ${user?.is_verified ? colors.green : colors.alertColor}30`,
            }}
          >
            {React.createElement(
              user?.is_verified ? FiCheck : FiInfo as any,
              { size: 20, color: user?.is_verified ? colors.green : colors.alertColor }
            )}
            <div className="flex-1">
              <CText type="M14" color={user?.is_verified ? colors.green : colors.alertColor}>
                {user?.is_verified ? 'Verified Expert' : 'Pending Verification'}
              </CText>
              <CText type="r12" color={colors.grayScale500}>
                {user?.is_verified
                  ? 'Your credentials have been verified by our team'
                  : 'Our team will verify your credentials within 24-48 hours'}
              </CText>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div
              className="mb-4 p-3 rounded-xl flex items-center gap-2"
              style={{
                backgroundColor: message.type === 'success' ? colors.green + '20' : colors.alertColor + '20',
              }}
            >
              {React.createElement(
                message.type === 'success' ? FiCheck : FiX as any,
                { size: 18, color: message.type === 'success' ? colors.green : colors.alertColor }
              )}
              <CText
                type="r14"
                color={message.type === 'success' ? colors.green : colors.alertColor}
              >
                {message.text}
              </CText>
            </div>
          )}

          {/* Credentials Form */}
          <div
            className="rounded-2xl p-5 mb-6"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: colors.primary + '15' }}
              >
                {React.createElement(FiShield as any, { size: 22, color: colors.primary })}
              </div>
              <div>
                <CText type="B16" color={colors.textColor}>
                  Registration Details
                </CText>
                <CText type="r12" color={colors.grayScale500}>
                  Your SEBI registration information
                </CText>
              </div>
            </div>

            {/* SEBI Registration Number */}
            <div className="mb-4">
              <CText type="M12" color={colors.textColor} style={{ marginBottom: 8 }}>
                SEBI Registration Number *
              </CText>
              <input
                type="text"
                value={regNumber}
                onChange={(e) => setRegNumber(e.target.value.toUpperCase())}
                placeholder="e.g., INH000012345"
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{
                  backgroundColor: colors.backgroundColor,
                  color: colors.textColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              />
              <CText type="r10" color={colors.grayScale500} style={{ marginTop: 4 }}>
                Enter your SEBI registration number as per your certificate
              </CText>
            </div>

            {/* Qualification */}
            <div className="mb-4">
              <CText type="M12" color={colors.textColor} style={{ marginBottom: 8 }}>
                Qualification Type *
              </CText>
              <select
                value={qualification}
                onChange={(e) => setQualification(e.target.value)}
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{
                  backgroundColor: colors.backgroundColor,
                  color: colors.textColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              >
                <option value="">Select qualification</option>
                {SEBI_QUALIFICATIONS.map((qual) => (
                  <option key={qual} value={qual}>
                    {qual}
                  </option>
                ))}
              </select>
            </div>

            {/* Years of Experience */}
            <div className="mb-4">
              <CText type="M12" color={colors.textColor} style={{ marginBottom: 8 }}>
                Years of Experience
              </CText>
              <input
                type="number"
                value={yearsOfXp}
                onChange={(e) => setYearsOfXp(e.target.value)}
                min="0"
                max="50"
                placeholder="0"
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{
                  backgroundColor: colors.backgroundColor,
                  color: colors.textColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              />
            </div>

            {/* Document Upload (placeholder) */}
            <div className="mb-4">
              <CText type="M12" color={colors.textColor} style={{ marginBottom: 8 }}>
                Upload Certificate (Optional)
              </CText>
              <div
                className="p-6 rounded-xl border-2 border-dashed text-center cursor-pointer hover:opacity-80 transition-opacity"
                style={{ borderColor: colors.borderColor }}
              >
                {React.createElement(FiUpload as any, { size: 24, color: colors.grayScale500, className: 'mx-auto mb-2' })}
                <CText type="r12" color={colors.grayScale500}>
                  Click to upload or drag & drop
                </CText>
                <CText type="r10" color={colors.grayScale400}>
                  PDF or Image (max 5MB)
                </CText>
              </div>
            </div>
          </div>

          {/* Verify SEBI Link */}
          <a
            href="https://www.sebi.gov.in/sebiweb/other/OtherAction.do?doRecognisedFpi=yes&intmId=13"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 mb-6 p-4 rounded-xl"
            style={{
              backgroundColor: colors.primary + '10',
              border: `1px solid ${colors.primary}30`,
            }}
          >
            {React.createElement(FiExternalLink as any, { size: 16, color: colors.primary })}
            <CText type="M12" color={colors.primary}>
              Verify SEBI Registration on Official Website
            </CText>
          </a>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 rounded-2xl font-semibold transition-all"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save Credentials'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SebiCredentials;
